import { useEffect, useState, useCallback } from "react";
import { Loader2, Ban, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ferr, Badge, money, date, Modal, downloadPdf, SearchBar, Table } from "../shared";

function UserDrawer({ userId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/users/${userId}`).then((r) => setData(r.data)).catch((e) => ferr(e, "Failed to load")); }, [userId]);
  return (
    <Modal title={data ? data.user.email : "Customer"} onClose={onClose}>
      {!data ? <Loader2 className="animate-spin text-mortar" /> : (
        <div className="space-y-5" data-testid="admin-user-drawer">
          <div className="card-mtr !p-4">
            <p className="font-bold text-navy">{data.user.name || "—"}</p>
            <p className="text-[13px] text-mortar">{data.user.company_name || "No company"}</p>
            <p className="text-[12px] text-mortar mt-1">{data.user.company_phone} {data.user.license_number && `· Lic ${data.user.license_number}`}</p>
            <p className="text-[12px] text-mortar mt-1">Role: {data.user.role} · {data.user.disabled ? "Disabled" : "Active"}</p>
          </div>
          <div>
            <p className="font-bold text-navy text-[14px] mb-2">Reports ({data.reports.length})</p>
            <div className="space-y-2">
              {data.reports.map((r) => (
                <div key={r.report_id} className="bg-white rounded-input p-3 border border-gravel flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-navy truncate">{r.address?.full || r.report_id}</p>
                    <p className="text-[11px] text-mortar">{money(r.customer_price)} · {date(r.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge value={r.status} />
                    {r.status === "complete" && <button onClick={() => downloadPdf(r.report_id)} className="icon-btn" title="PDF"><Download size={15} /></button>}
                  </div>
                </div>
              ))}
              {!data.reports.length && <p className="text-[13px] text-mortar">No reports.</p>}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function UsersPanel() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { q } }); setRows(data); }
    catch (e) { ferr(e, "Failed to load users."); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const toggleDisable = async (u) => {
    try { await api.patch(`/admin/users/${u.user_id}`, { disabled: !u.disabled }); toast.success(u.disabled ? "Account enabled." : "Account disabled."); load(); }
    catch (e) { ferr(e, "Action failed."); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This removes their reports and transactions.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Account deleted."); setActive(null); load(); }
    catch (e) { ferr(e, "Delete failed."); }
  };

  return (
    <div data-testid="admin-users">
      <SearchBar value={q} onChange={setQ} placeholder="Search by email, name, company…" testid="admin-users-search" />
      <Table cols={["Email", "Name", "Company", "Role", "Reports", "Status", ""]} loading={loading} empty={!rows.length}>
        {rows.map((u) => (
          <tr key={u.user_id} className="border-t border-gravel hover:bg-fieldstone cursor-pointer" data-testid={`admin-user-row-${u.user_id}`}>
            <td className="td" onClick={() => setActive(u.user_id)}>{u.email}</td>
            <td className="td" onClick={() => setActive(u.user_id)}>{u.name || "—"}</td>
            <td className="td" onClick={() => setActive(u.user_id)}>{u.company_name || "—"}</td>
            <td className="td">{u.role}</td>
            <td className="td">{u.report_count}</td>
            <td className="td">{u.disabled ? <Badge value="failed" /> : <Badge value="paid" />}</td>
            <td className="td text-right whitespace-nowrap">
              {u.role !== "admin" && (
                <>
                  <button onClick={() => toggleDisable(u)} className="icon-btn" title={u.disabled ? "Enable" : "Disable"} data-testid={`admin-user-toggle-${u.user_id}`}><Ban size={15} /></button>
                  <button onClick={() => remove(u)} className="icon-btn text-rose-600" title="Delete" data-testid={`admin-user-delete-${u.user_id}`}><Trash2 size={15} /></button>
                </>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {active && <UserDrawer userId={active} onClose={() => setActive(null)} />}
    </div>
  );
}
